<template>
  <div class="complex-filter-row">
    <template v-if="isFieldMissing">
      <BittsTooltip
        placement="top"
        :mount-to-body="true"
        class="flex items-stretch self-stretch"
        default-slot-classes="flex items-stretch"
      >
        <BittsFilterSelect
          :model-value="filter.value"
          :options="[{ label: 'Hidden field', value: filter.value }]"
          :disabled="true"
          class="complex-filter-row__field complex-filter-row__field--hidden"
        />
        <template #title
          >This filter uses fields not shared with your organization</template
        >
      </BittsTooltip>
    </template>
    <template v-else>
      <BittsFilterSelect
        :model-value="filter.value"
        :options="selectableFieldOptions"
        :disabled="isDisabled || !canDelete"
        :use-search="useFieldSearch"
        placeholder="Select field"
        class="complex-filter-row__field"
        @update:model-value="onFieldChange"
      />

      <ComplexFilterOperatorSelector
        :key="String(filter.value)"
        :model-value="filter.operator"
        :data-type="selectedFieldDataType"
        :disabled="
          isDisabled || !selectedField || !!selectedField?.disableOperator
        "
        :allowed-operators="selectedField?.allowedOperators"
        class="complex-filter-row__operator"
        :class="{
          'as-operand': isUnaryOperator || !selectedField,
        }"
        @update:model-value="updateOperator"
      />

      <template
        v-if="selectedField && filter.operator && operandInputMode !== 'none'"
      >
        <slot name="operand">
          <BittsFilterSelect
            v-if="selectedFieldDataType === 'boolean'"
            :model-value="operandAsBoolean"
            :options="BOOLEAN_OPTIONS"
            :disabled="isDisabled"
            :use-search="false"
            class="complex-filter-row__boolean-operand"
            @update:model-value="onBooleanChange"
          />

          <OurSegmentFilterSelect
            v-else-if="selectedFieldDataType === 'our_segment_filter'"
            :model-value="operandAsOurSegmentFilter"
            :disabled="isDisabled"
            class="complex-filter-row__segment-operand"
            @update:model-value="updateOurSegmentOperand"
          />

          <PartnerSegmentFilterSelect
            v-else-if="selectedFieldDataType === 'partner_segment_filter'"
            :model-value="operandAsPartnerSegmentFilter"
            :disabled="isDisabled"
            class="complex-filter-row__segment-operand"
            @update:model-value="updatePartnerSegmentOperand"
          />

          <BittsRangePicker
            v-else-if="selectedFieldDataType === 'datetime'"
            :initial-date="operandAsDateString"
            :use-single-date-picker="true"
            :disabled="isDisabled"
            format="YYYY-MM-DD"
            placeholder="Select date"
            class="complex-filter-row__date-operand"
            @change="updateDateOperand"
          />

          <BittsFilterMultiselect
            v-else-if="operandInputMode === 'multiple' && !useTagsForMultiple"
            :key="`multiple-${String(filter.value)}-${filter.operator}`"
            :model-value="operandAsArray"
            :options="fieldValueOptions"
            :disabled="isDisabled"
            :use-search="true"
            :use-overflow="true"
            :loading="hasFetchOptions && isLoadingOptions && !hasStaticOptions"
            placeholder="Select options"
            class="complex-filter-row__operand"
            @update:model-value="updateArrayOperand"
            @search-changed="onOperandSearch"
          />

          <BittsFilterTagInput
            v-else-if="operandInputMode === 'tags' || useTagsForMultiple"
            :key="`tags-${String(filter.value)}-${filter.operator}`"
            :model-value="operandAsStringArray"
            :disabled="isDisabled"
            placeholder="Enter values"
            class="complex-filter-row__operand complex-filter-row__tag-operand"
            @update:model-value="updateStringArrayOperand"
          />

          <BittsInput
            v-else
            :key="`single-${String(filter.value)}-${filter.operator}`"
            :model-value="operandAsSingle"
            :disabled="isDisabled"
            :placeholder="inputPlaceholder"
            name="operand-value"
            size="small"
            class="complex-filter-row__operand"
            @keydown="handleNumberKeydown"
            @update:model-value="updateSingleOperand"
          />
        </slot>
      </template>
    </template>

    <BittsTooltip
      v-if="!hideDelete && !canDelete && !props.areFiltersReadOnly"
      placement="top"
      :mount-to-body="true"
    >
      <BittsButton
        variant="ghost"
        size="small"
        type="neutral"
        :center-icon="['fak', 'delete']"
        aria-label="Delete filter"
        :aria-disabled="true"
        class="ml-8"
      />
      <template #title>This filter is required</template>
    </BittsTooltip>
    <BittsButton
      v-else-if="!hideDelete && !props.areFiltersReadOnly"
      variant="ghost"
      size="small"
      type="neutral"
      :center-icon="['fak', 'delete']"
      aria-label="Delete filter"
      :disabled="isDisabled"
      class="ml-8"
      @click="$emit('delete')"
    />
  </div>
</template>

<script
  setup
  lang="ts"
  generic="TField extends string | number, TOperand = unknown"
>
import {
  BittsButton,
  BittsFilterMultiselect,
  BittsFilterSelect,
  BittsFilterTagInput,
  BittsInput,
  BittsRangePicker,
  BittsTooltip,
} from '@crossbeam/bitts';
import type {
  BittsMenuGroup,
  BittsRangePickerChangeValue,
} from '@crossbeam/bitts';

import { useDebounceFn } from '@vueuse/core';
import { DateTime } from 'luxon';
import { computed, ref, watch } from 'vue';

import OurSegmentFilterSelect from '@/components/reports/OurSegmentFilterSelect.vue';
import PartnerSegmentFilterSelect from '@/components/reports/PartnerSegmentFilterSelect.vue';

import {
  getDefaultsForDataType,
  getOperatorConfig,
  requiresSequentialOperand,
} from '@/constants/filter-operators';
import type {
  FilterFieldOption,
  FilterRowState,
  ReportFilterOperatorTypes,
  SelectOption,
} from '@/types/filters';
import type {
  ReportOurSegmentFilters,
  ReportPartnerSegmentFilters,
} from '@/types/reports';
import { formatNumberWithCommas } from '@/utils';

import ComplexFilterOperatorSelector from './ComplexFilterOperatorSelector.vue';

const props = withDefaults(
  defineProps<{
    fieldOptions: FilterFieldOption<TField>[];
    disabled?: boolean;
    hideDelete?: boolean;
    canDelete?: boolean;
    useFieldSearch?: boolean;
    areFiltersReadOnly?: boolean;
  }>(),
  {
    disabled: false,
    hideDelete: false,
    canDelete: true,
    useFieldSearch: true,
    areFiltersReadOnly: false,
  },
);

defineEmits<{
  delete: [];
}>();

const filter = defineModel<FilterRowState<TField, TOperand>>({
  required: true,
});

const isDisabled = computed(() => props.disabled || props.areFiltersReadOnly);

const BOOLEAN_OPTIONS = [
  { label: 'true', value: true },
  { label: 'false', value: false },
] as const;

const selectableFieldOptions = computed<
  (SelectOption | BittsMenuGroup<SelectOption>)[]
>(() => {
  const options: SelectOption[] = props.fieldOptions.map((f) => ({
    label: f.label,
    value: f.value,
  }));

  // Include current value so it displays before options load
  const currentValue = filter.value.value;
  if (
    currentValue !== undefined &&
    !options.some((o) => o.value === currentValue)
  ) {
    options.unshift({ label: String(currentValue), value: currentValue });
  }

  if (!props.fieldOptions.some((f) => f.group)) return options;

  const groupByValue = new Map<SelectOption['value'], string | undefined>(
    props.fieldOptions.map((f) => [f.value, f.group]),
  );

  const ungrouped: SelectOption[] = [];
  const grouped = new Map<string, SelectOption[]>();
  options.forEach((option) => {
    const group = groupByValue.get(option.value);
    if (!group) {
      ungrouped.push(option);
      return;
    }
    const bucket = grouped.get(group);
    if (bucket) {
      bucket.push(option);
    } else {
      grouped.set(group, [option]);
    }
  });

  const groups = [...grouped].map(([label, groupOptions]) => ({
    label,
    useBorder: true,
    options: groupOptions,
  }));
  return [...ungrouped, ...groups];
});

const selectedField = computed(() =>
  props.fieldOptions.find((f) => f.value === filter.value.value),
);

const isFieldMissing = computed(
  () =>
    filter.value.value !== undefined &&
    !props.fieldOptions.some((f) => f.value === filter.value.value),
);

const selectedFieldDataType = computed(
  () => selectedField.value?.dataType ?? 'string',
);

const operatorConfig = computed(() => {
  if (!filter.value.operator) return undefined;
  return getOperatorConfig(selectedFieldDataType.value, filter.value.operator);
});

const operandInputMode = computed(
  () => operatorConfig.value?.inputMode ?? 'none',
);

const isUnaryOperator = computed(() =>
  ['is empty', 'is not empty'].includes(filter.value.operator ?? ''),
);

const hasFetchOptions = computed(() =>
  Boolean(selectedField.value?.fetchOptions),
);

// Use tag input for string "multiple" mode when:
// - Field explicitly requests tag input (e.g., partner fields), OR
// - No options available and no fetchOptions, OR
// - fetchOptions returned empty results (source-data endpoint had no values)
const useTagsForMultiple = computed(() => {
  if (operandInputMode.value !== 'multiple') return false;
  if (selectedFieldDataType.value !== 'string') return false;

  // Explicit flag takes precedence
  if (selectedField.value?.useTagInput) return true;

  const hasStaticOpts = (selectedField.value?.options?.length ?? 0) > 0;

  // No options source at all
  if (!hasStaticOpts && !selectedField.value?.fetchOptions) return true;

  // Initial fetch (empty query) returned no results and no static options
  if (
    selectedField.value?.fetchOptions &&
    !isLoadingOptions.value &&
    initialFetchEmpty.value &&
    !hasStaticOpts
  ) {
    return true;
  }

  return false;
});

// =============================================================================
// Async Options Fetching
// =============================================================================

const fetchedOptions = ref<SelectOption[]>([]);
const isLoadingOptions = ref(false);
const initialFetchEmpty = ref(false);

let latestOptionsRequest = 0;

// Cache labels so placeholder options retain human-readable names
const labelCache = new Map<string | number, string>();

function cacheLabels(options: SelectOption[]) {
  for (const opt of options) {
    labelCache.set(opt.value, opt.label);
  }
}

const hasStaticOptions = computed(
  () => (selectedField.value?.options?.length ?? 0) > 0,
);

const fieldValueOptions = computed(() => {
  const staticOptions = selectedField.value?.options ?? [];
  const currentSelection = operandAsArray.value;

  // Start with static options
  const seen = new Set(staticOptions.map((o) => o.value));
  const merged = [...staticOptions];

  // Add fetched options
  for (const opt of fetchedOptions.value) {
    if (!seen.has(opt.value)) {
      merged.push(opt);
      seen.add(opt.value);
    }
  }

  // Preserve currently selected values that aren't in the list
  // (create placeholder options so they remain visible)
  for (const val of currentSelection) {
    if (!seen.has(val)) {
      const cachedLabel = labelCache.get(val);
      merged.push({ label: cachedLabel ?? String(val), value: val });
      seen.add(val);
    }
  }

  // Some fields (e.g. partner score level) declare their options in an
  // intentional logical order that shouldn't be reordered alphabetically.
  if (selectedField.value?.preserveOptionOrder) {
    return merged;
  }

  return merged.sort((a, b) => a.label.localeCompare(b.label));
});

async function fetchOptionsForField(query = '') {
  const field = selectedField.value;
  if (!field?.fetchOptions) return;

  const requestId = ++latestOptionsRequest;
  isLoadingOptions.value = true;
  try {
    const options = await field.fetchOptions(query);
    // Guard against a superseded fetch: a newer request started, or the field
    // changed during the await
    if (requestId !== latestOptionsRequest) return;
    if (selectedField.value?.value !== field.value) return;
    cacheLabels(options);
    fetchedOptions.value = options;
    if (query === '') {
      initialFetchEmpty.value = options.length === 0;
    }
  } finally {
    if (
      requestId === latestOptionsRequest &&
      selectedField.value?.value === field.value
    ) {
      isLoadingOptions.value = false;
    }
  }
}

const debouncedFetchOptions = useDebounceFn(fetchOptionsForField, 300);

function onOperandSearch(query: string) {
  if (selectedField.value?.fetchOptions) {
    debouncedFetchOptions(query);
  }
}

// Fetch initial options when field changes
watch(
  () => filter.value.value,
  (newFieldValue) => {
    fetchedOptions.value = [];
    initialFetchEmpty.value = false;
    if (newFieldValue !== undefined) {
      const field = props.fieldOptions.find((f) => f.value === newFieldValue);
      if (field?.options) {
        cacheLabels(field.options);
      }
      if (field?.fetchOptions) {
        fetchOptionsForField('');
      }
    }
  },
  { immediate: true },
);

// =============================================================================

const inputPlaceholder = computed(() =>
  selectedFieldDataType.value === 'number' ? 'Enter number' : 'Enter value',
);

const operandAsBoolean = computed<boolean | undefined>(() => {
  const op = filter.value.operand;
  const first = Array.isArray(op) ? op[0] : op;
  if (first === true || first === 'true') return true;
  if (first === false || first === 'false') return false;
  return undefined;
});

const operandAsArray = computed(() => {
  const op = filter.value.operand;
  if (Array.isArray(op)) {
    return op.filter(
      (i): i is string | number =>
        typeof i === 'string' || typeof i === 'number',
    );
  }
  if (typeof op === 'string' || typeof op === 'number') return [op];
  return [];
});

const operandAsStringArray = computed(() => {
  const op = filter.value.operand;
  if (Array.isArray(op)) {
    return op.filter((i): i is string => typeof i === 'string');
  }
  if (typeof op === 'string') return [op];
  return [];
});

/**
 * Strictly returns a string to satisfy BittsInput props.
 * Handles number formatting with commas.
 */
const operandAsSingle = computed<string>(() => {
  const op = filter.value.operand;
  const rawValue = Array.isArray(op) ? op[0] : op;

  if (
    rawValue == null ||
    typeof rawValue === 'boolean' ||
    typeof rawValue === 'object'
  ) {
    return '';
  }

  if (typeof rawValue === 'number') {
    return formatNumberWithCommas(rawValue);
  }

  return String(rawValue);
});

const operandAsDateString = computed(() => {
  const op = filter.value.operand;
  if (typeof op === 'string') return op.split('T')[0];
  if (Array.isArray(op) && typeof op[0] === 'string') {
    return op[0].split('T')[0];
  }
  return undefined;
});

function hasItemsArray(op: unknown): op is { items: unknown[] } {
  return (
    op !== null &&
    typeof op === 'object' &&
    !Array.isArray(op) &&
    'items' in op &&
    Array.isArray((op as { items: unknown }).items)
  );
}

function isReportOurSegmentFilters(op: unknown): op is ReportOurSegmentFilters {
  return hasItemsArray(op);
}

const operandAsOurSegmentFilter = computed<ReportOurSegmentFilters>(() => {
  const op = filter.value.operand;
  if (isReportOurSegmentFilters(op)) return op;
  return { items: [], operator: 'OR' };
});

function isReportPartnerSegmentFilters(
  op: unknown,
): op is ReportPartnerSegmentFilters {
  return (
    hasItemsArray(op) &&
    op.items.every(
      (item) => typeof item === 'object' && item !== null && 'segment' in item,
    )
  );
}

const operandAsPartnerSegmentFilter = computed<ReportPartnerSegmentFilters>(
  () => {
    const op = filter.value.operand;
    if (isReportPartnerSegmentFilters(op)) return op;
    return { items: [], operator: 'OR' };
  },
);

function onFieldChange(newValue: SelectOption['value'] | undefined) {
  if (newValue === undefined) return;

  const fieldOption = props.fieldOptions.find((f) => f.value === newValue);
  const dataType = fieldOption?.dataType ?? 'string';
  const defaults = getDefaultsForDataType(dataType);

  // `allowedOperators` constrains which operators a field accepts; it does not
  // rank them. Keep the data type's default (`>=` for numbers and dates, so a
  // range can be built) whenever the field allows it.
  const { allowedOperators } = fieldOption ?? {};
  const operator = allowedOperators?.includes(defaults.operator)
    ? defaults.operator
    : (allowedOperators?.[0] ?? defaults.operator);

  filter.value = {
    ...filter.value,
    value: newValue as TField,
    operator,
    operand: defaults.operand as TOperand,
  };
}

function onBooleanChange(val?: boolean) {
  if (val === undefined) return;
  filter.value = {
    ...filter.value,
    operator: filter.value.operator,
    operand: [val] as TOperand,
  };
}

function updateOperator(newOperator?: ReportFilterOperatorTypes) {
  if (!newOperator) return;

  const isUnary = ['is empty', 'is not empty'].includes(newOperator);
  const opConfig = getOperatorConfig(selectedFieldDataType.value, newOperator);
  const prevConfig = filter.value.operator
    ? getOperatorConfig(selectedFieldDataType.value, filter.value.operator)
    : undefined;

  // Operand shape follows the operator, not the input mode: number and
  // datetime fields render `in` and `>=` through the same single input, so
  // inputMode cannot tell a sequential operand from a scalar one.
  const needsArray = requiresSequentialOperand(newOperator);
  const hadArray = filter.value.operator
    ? requiresSequentialOperand(filter.value.operator)
    : false;

  const isSwitchingBetweenMultipleAndTags =
    (prevConfig?.inputMode === 'multiple' && opConfig?.inputMode === 'tags') ||
    (prevConfig?.inputMode === 'tags' && opConfig?.inputMode === 'multiple');

  const current = filter.value.operand;
  let nextOperand: unknown = current;

  if (isUnary) {
    nextOperand = null;
  } else if (isSwitchingBetweenMultipleAndTags) {
    nextOperand = [];
  } else if (needsArray && !hadArray && !Array.isArray(current)) {
    const isPrimitive =
      typeof current === 'string' || typeof current === 'number';
    nextOperand = isPrimitive ? [current] : [];
  } else if (!needsArray && hadArray && Array.isArray(current)) {
    nextOperand = current.length ? current[0] : null;
  }

  filter.value = {
    ...filter.value,
    operator: newOperator,
    operand: nextOperand as TOperand,
  };
}

function handleNumberKeydown(e: KeyboardEvent) {
  if (selectedFieldDataType.value !== 'number') return;

  // allow shortcuts (Ctrl+A, Ctrl+C) and special keys (Backspace, Arrows, Enter, etc)
  if (e.ctrlKey || e.metaKey || e.key.length > 1) return;

  // allow positive/negative numbers and decimal point
  if (!/[\d.-]/.test(e.key)) e.preventDefault();
}

function updateSingleOperand(val: string) {
  let finalVal: string | number | null = val;

  if (selectedFieldDataType.value === 'number' && val !== '') {
    // Strip non-numeric chars (keep digits, decimal, minus)... basically hack BittsInput into a number input
    const cleaned = val.replace(/[^\d.-]/g, '');
    const num = Number(cleaned);
    finalVal = cleaned && !isNaN(num) ? num : null;
  } else if (val === '') {
    finalVal = null;
  }

  filter.value = { ...filter.value, operand: finalVal as TOperand };
}

function updateArrayOperand(val: (string | number)[]) {
  // Required filters (canDelete: false) cannot have their values fully cleared
  if (!props.canDelete && val.length === 0) return;
  filter.value = { ...filter.value, operand: val as TOperand };
}

function updateStringArrayOperand(val: string[]) {
  if (!props.canDelete && val.length === 0) return;
  filter.value = { ...filter.value, operand: val as TOperand };
}

function updateDateOperand(date: BittsRangePickerChangeValue) {
  if (Array.isArray(date)) return;
  if (!date) {
    filter.value = { ...filter.value, operand: null as TOperand };
    return;
  }

  const isoStr = date.toISOString();
  const luxonDate = DateTime.fromISO(isoStr).startOf('day').toISO();
  filter.value = { ...filter.value, operand: (luxonDate || null) as TOperand };
}

function updateOurSegmentOperand(val?: ReportOurSegmentFilters) {
  filter.value = { ...filter.value, operand: val as TOperand };
}

function updatePartnerSegmentOperand(val?: ReportPartnerSegmentFilters) {
  filter.value = { ...filter.value, operand: val as TOperand };
}
</script>

<style scoped lang="pcss">
@reference "../../css/theme.css";

.complex-filter-row {
  @apply flex items-center gap-0 min-w-0;
}

.complex-filter-row__field {
  @apply min-w-[120px] max-w-[180px];
  :deep(.bitts-filter-select__trigger) {
    @apply rounded-r-none border-r-0 z-10 relative;
  }
  :deep(.bitts-filter-select__selected-item) {
    @apply overflow-hidden;
  }
  :deep(.bitts-filter-select__label) {
    @apply truncate;
  }
}

.complex-filter-row__field--hidden {
  :deep(.bitts-filter-select__trigger) {
    @apply rounded-r-lg border-r;
  }
  :deep(.bitts-filter-select__label) {
    @apply text-neutral-text-placeholder;
  }
}

.complex-filter-row__boolean-operand {
  :deep(.bitts-filter-select__trigger) {
    @apply rounded-l-none border-l-0;
  }
}

.complex-filter-row__operator {
  :deep(.bitts-filter-select__trigger) {
    @apply rounded-none z-10 relative;
    &:hover,
    &:focus-within {
      @apply z-20;
    }
  }
}

.complex-filter-row__operator.as-operand {
  :deep(.bitts-filter-select__trigger) {
    @apply rounded-r-lg border-r;
  }
}

.complex-filter-row__operand {
  @apply min-w-[120px];

  :deep(.bitts-filter-multiselect__trigger) {
    @apply rounded-l-none border-l-0 min-w-0;
  }

  :deep(.bitts-filter-multiselect__selected-items) {
    @apply min-w-0;
  }

  &.c-bitts-input {
    @apply w-min min-w-[140px];

    :deep(.c-bitts-input__input) {
      @apply rounded-l-none border-l-0;
    }

    :deep(.c-bitts-input__input.ant-input-affix-wrapper) {
      @apply outline-none shadow-none;
      &:hover,
      &:focus-within {
        @apply border-neutral-border outline-none shadow-none;
      }
    }
  }
}

.complex-filter-row__tag-operand {
  :deep(.bitts-filter-tag-input__trigger) {
    @apply rounded-l-none border-l-0 min-w-0;
  }
}

.complex-filter-row__date-operand {
  :deep(.ant-picker) {
    @apply rounded-l-none border-l-0;
  }
  :deep(.ant-picker:hover) {
    @apply outline-0 border-neutral-border;
  }
  :deep(.ant-picker-focused) {
    @apply outline-0 border-neutral-border shadow-none;
  }
}

.complex-filter-row__segment-operand {
  @apply min-w-[120px] flex-1;

  :deep(.main-select) {
    @apply rounded-l-none border-l-0;

    &.open {
      @apply outline-none border-neutral-border;
    }
  }
}

:deep(.c-bitts-input__input.disabled) {
  @apply bg-white! opacity-60! pointer-events-none;
}

:deep(.ant-input.ant-input-disabled) {
  @apply text-neutral-text-strong;
}

:deep(.bitts-range-picker .ant-picker-disabled) {
  @apply bg-white opacity-60;
}

:deep(.bitts-range-picker .ant-picker-disabled .ant-picker-input > input) {
  @apply text-neutral-text-strong;
}
</style>
